import React, { ReactNode } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { bffAuthService } from "../../services/BffAuthService";
import { ROUTES } from "../../home/constants/routes";

// 暂时屏蔽登录，直接放行
const BYPASS_AUTH = true;

interface AuthRouteProps {
  children: ReactNode;
}

export const AuthRoute = ({ children }: AuthRouteProps) => {
  const [isAuthenticated, setIsAuthenticated] = React.useState<boolean | null>(null);
  const location = useLocation();
  const navigate = useNavigate();

  // 暂时屏蔽登录：直接放行
  if (BYPASS_AUTH) {
    return <>{children}</>;
  }

  // 初始化认证状态
  React.useEffect(() => {
    let isMounted = true;

    const initAuth = async () => {
      try {
        const authenticated = await bffAuthService.initSession();
        if (isMounted) {
          setIsAuthenticated(authenticated);
        }
      } catch (error) {
        console.error('Auth initialization failed:', error);
        if (isMounted) {
          setIsAuthenticated(false);
        }
      }
    };

    initAuth();

    return () => {
      isMounted = false;
    };
  }, []);

  // 监听认证状态变化
  React.useEffect(() => {
    if (isAuthenticated === null) return; // 还在初始化

    const unsubscribe = bffAuthService.subscribe((authenticated: boolean) => {
      setIsAuthenticated(authenticated);
    });

    return unsubscribe;
  }, [isAuthenticated]);

  // 处理重定向逻辑
  React.useEffect(() => {
    if (isAuthenticated) {
      const redirectTo = sessionStorage.getItem('oidc_redirect_to');
      if (redirectTo) {
        const decodedPath = decodeURIComponent(redirectTo);
        sessionStorage.removeItem('oidc_redirect_to');

        if (location.pathname + location.search !== decodedPath) {
          navigate(decodedPath, { replace: true });
        }
      }
    } else if (isAuthenticated === false) {
      // 未认证，触发登录流程
      const redirectTo = encodeURIComponent(location.pathname + location.search);
      sessionStorage.setItem('oidc_redirect_to', redirectTo);
      bffAuthService.login();
    }
  }, [isAuthenticated, navigate, location.pathname, location.search]);

  // 显示加载状态（正在初始化或跳转）
  if (isAuthenticated === null || isAuthenticated === false) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        flexDirection: 'column'
      }}>
        <h2>正在跳转到登录页面...</h2>
        <p>请稍候，系统正在为您安全登录。</p>
      </div>
    );
  }

  // 已认证，渲染子组件
  return <>{children}</>;
};

export default AuthRoute;